import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import FormDialog from '../../../components/ui/FormDialog'
import FormField from '../../../components/ui/FormField'
import UserMultiPicker, { type PickableUser } from '../../../components/ui/UserMultiPicker'
import { useToast } from '../../../components/ui/ToastProvider'
import { useProxyMode } from '../../proxy/useProxyMode'
import { getApiErrorCode } from '../../../api/apiError'
import { searchTodoShareUsers } from '../api/todoApi'
import { rowActionLabel } from '../../../lib/deleteLabel'
import { useTodoShares } from '../hooks/useTodoLists'
import type { TodoList, TodoShare } from '../types'

/**
 * Erişim kaldırma onayının <b>grubu</b> — aşağıdaki {@code <ConfirmDialog group=…>} örneğiyle
 * eşleşir.
 *
 * <p><b>Neden grup şart:</b> {@code confirmDialog()} bir servis çağrısıdır, DOM'daki belirli bir
 * örneğe değil <b>tüm</b> mount edilmiş {@code <ConfirmDialog>}'lara yayın yapar. Bu pop-up
 * {@code TodoListsPage}'in içinde açılıyor ve o sayfanın da kendi (grupsuz) örneği var; grupsuz bir
 * çağrı ikisinde birden açılıyor, kullanıcı üst üste <b>iki</b> onay penceresi görüyordu (kullanıcı
 * bulgusu 26.09.2026). Grup, çağrıyı tek örneğe adresler.
 */
const SHARE_REMOVE_GROUP = 'todo-share-remove'

interface Props {
  /** Paylaşımı açılan liste; `null` → pop-up kapalı (sorgu da koşmaz). */
  list: TodoList | null
  onHide: () => void
}

/**
 * <b>Paylaşım pop-up'ı</b> (113, US2) — "Listeyi Görebilenler" + kişi ekleme.
 *
 * <p>Pop-up, listeyi <b>görebilen herkese</b> açılır — ama <b>kişi ekleme/çıkarma yalnız liste
 * SAHİBİNDEDİR</b> (kullanıcı kararı 01.10.2026). Paylaşılan kullanıcı burada yalnız <b>kimlerin
 * gördüğünü</b> okur: yeni kişi ekleyemez, kimseyi çıkaramaz — <b>kendi satırını bile</b>
 * (30.09.2026'da açılan FR-025b istisnası aynı kararla kapatıldı). Zincirleme paylaşım, sahibin
 * haberi olmadan listeyi üçüncü kişilere açıyordu.
 * <b>Liste sahibi seçicide çıkmaz</b> — o zaten listeyi görüyor ve sunucu onu eklemeyi reddeder.
 *
 * <p>Sunucu aynı kuralı bağımsız uygular ({@code TodoListShareService.requireOwned}); buradaki gizleme
 * yalnız kullanıcıyı yapamayacağı işe yöneltmemek içindir.
 *
 * <p><b>Zaten ekli kişiler seçicide görünmez</b> — eleme <b>sunucuda</b> yapılır (arama ucuna
 * `listId` gider). İstemcide elemek ikinci bir ölçüt yaratır ve biri değiştiğinde öteki bayatlardı.
 * Bu, analistin kısmi-tekil indeksi istememesi üzerine daha da önem kazandı: tekillik artık yalnız
 * arayüz + servis kontrolüyle korunuyor (bkz. {@code db/SEMA-IHTIYACI.md}).
 *
 * <p>Ekleme <b>hepsi-ya-hiç</b>tır: sunucu geçersiz tek bir seçim için isteğin tamamını reddeder,
 * ekran da seçimi <b>korur</b> — kullanıcı neyi seçtiğini görmeye devam eder ve düzeltip yeniden
 * gönderebilir; seçimi temizlemek, hatayı gidermenin tek yolunu ortadan kaldırırdı.
 */
export default function TodoShareDialog({ list, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const listId = list?.id ?? null
  const { list: shares, add, remove } = useTodoShares(listId)
  /**
   * Kişi ekleme/çıkarma yalnız **liste sahibinde** (01.10.2026). Ölçüt sunucunun verdiği `owned`
   * bayrağıdır — istemcide sahiplik yeniden hesaplanmaz; sunucu da aynı kuralı bağımsız uygular.
   *
   * <p><b>Kısıtlı Yetkili asistan da sahip sayılmaz</b> (126, kullanıcı bildirimi 21.10.2026):
   * persona kipinde `owned` HEDEFİN sahipliğini anlatır ve yöneticinin kendi listesinde `true`
   * gelir, oysa asistanın yazma hakkı yok. Tek bayrağı daraltmak yetiyor çünkü pop-up'ın **tüm**
   * yazma yolu buna bağlı: kişi seçici, footer (`hideFooter={!owner}`) ve çıkarma düğmeleri.
   * Görüntüleyen listesi okunur kalır — listenin kimlerle paylaşıldığını görmek bir okumadır.
   */
  const { readOnly } = useProxyMode()
  const owner = list?.owned === true && !readOnly

  const [selected, setSelected] = useState<PickableUser[]>([])
  const [error, setError] = useState<string | null>(null)

  // Pop-up her açılışta temiz başlar: önceki listenin seçimi burada kalsaydı kullanıcı, yanlış
  // listeye kişi eklemeye bir tık uzakta olurdu.
  useEffect(() => {
    if (listId != null) {
      setSelected([])
      setError(null)
    }
  }, [listId])

  const viewers = shares.data ?? []

  /**
   * Hata <b>pop-up'ın içinde</b> gösterilir, toast olarak değil: kullanıcı seçimini düzeltmek için
   * zaten buradadır ve kapanıp giden bir bildirim, hangi seçimin sorunlu olduğunu okumaya vakit
   * bırakmazdı. Çakışma (409) ile geçersiz seçim (400) ayrı metinler alır — ikisi farklı düzeltme
   * gerektirir: biri "bu kişi zaten ekli", öteki "bu kişi eklenemez".
   */
  const onError = (err: unknown) => {
    const code = getApiErrorCode(err)
    if (code === 'DUPLICATE_NAME') {
      setError(t('todo.duplicateShare'))
      return
    }
    if (code === 'VALIDATION_ERROR') {
      setError(t('todo.shareNotAllowed'))
    }
  }

  const submit = () => {
    if (selected.length === 0) return
    setError(null)
    add.mutate(
      selected.map((u) => u.id),
      {
        onSuccess: () => {
          toast.success(t('todo.shared'))
          setSelected([])
        },
        onError,
      },
    )
  }

  /**
   * Erişimi kaldırma <b>onay ister</b>: geri alması yeni bir paylaşım eklemek demek ve satırlar
   * birbirine çok yakın — yanlış kişiyi çıkarmak kolay olurdu.
   *
   * <p>Onay metni kişinin adını taşır; düğmenin erişilebilir adı da öyle ({@code rowActionLabel}) —
   * sesli kontrol kullanıcısı için tek tanımlayıcı odur, beş satırda beş kez "Kaldır" ayırt edilemezdi.
   */
  const confirmRemove = (viewer: TodoShare) =>
    confirmDialog({
      group: SHARE_REMOVE_GROUP,
      message: t('todo.removeShareConfirm', { name: viewer.fullName }),
      header: t('todo.removeShare'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      rejectLabel: t('common.cancel'),
      accept: () =>
        remove.mutate(viewer.userId, {
          onSuccess: () => {
            toast.success(t('todo.shareRemoved'))
            // Pop-up AÇIK kalır. 30.09–01.10.2026 arasında burada "kendini çıkardıysa kapat" dalı
            // vardı; o yol kapandı: çıkarmayı artık yalnız SAHİP yapabiliyor ve sahip hiçbir zaman
            // "Görebilenler"de yer almaz (sunucu onu paylaşım olarak eklemeyi reddeder), yani
            // çıkaran kişi erişimini asla kaybetmez.
          },
        }),
    })

  return (
    <FormDialog
      visible={list != null}
      header={t('todo.shareTitle')}
      // Sahibi olmayan için pop-up SALT-OKUMADIR: gönderilecek bir aksiyon yok, footer da olmamalı
      // (kullanıcı bildirimi 01.10.2026 — alan gizliyken "Kullanıcı Ekle" düğmesi duruyordu).
      hideFooter={!owner}
      submitLabel={t('todo.addUser')}
      submitting={add.isPending}
      submitDisabled={add.isPending || selected.length === 0}
      onSubmit={submit}
      onHide={onHide}
    >
      <div className="admin-form todo-share">
        {/* Onay diyaloğu pop-up'ın İÇİNDE: PrimeReact overlay'i en son açılan diyaloğun üstünde
            çizer; dışarıda kalsaydı paylaşım pop-up'ının arkasında kalıp görünmezdi.

            `group` ZORUNLU — bkz. SHARE_REMOVE_GROUP: bu örnek, sayfanın kendi örneğiyle aynı anda
            mount hâlindedir ve grupsuz çağrı ikisini birden açardı. */}
        <ConfirmDialog group={SHARE_REMOVE_GROUP} />
        {/* Hangi listede çalışıldığı gövdenin en üstünde durur (dialog standardı `pop-subtitle`),
            başlığın yanında değil: uzun liste adları başlığı taşırıyor ve "Listeyi Görebilenler"
            ifadesini okunmaz hâle getiriyordu (kullanıcı bildirimi 25.09.2026). */}
        <p className="pop-subtitle">{list?.name}</p>

        {/* Alan ile düğme AYNI adı taşımamalı: sesli kontrol kullanıcısı "Kullanıcı Ekle"
            dediğinde hangisinin kastedildiği belirsiz kalırdı. Alan "Kullanıcı Ara", düğme
            "Kullanıcı Ekle".

            Etiket + hata kutusu ELLE kurulMAZ, paylaşılan `FormField`'dan tüketilir: kutu orada
            zaten her zaman DOM'dadır (koşullu eklenen bir canlı bölge duyurulmaz), `id`'si
            `${htmlFor}-err` olarak türetilir ve boşken `.ui-field-err:empty` ile hiç yer kaplamaz.
            Burada ikinci bir kopyasını yazmak Reuse Mandate'e aykırıydı — bileşenin kendi
            javadoc'u bunu adıyla söylüyor. Duyuru `polite`: `assertive` yalnız marjinal bir
            kazanç verirdi, paylaşılan davranıştan ayrışmak ise her ekranda farklı bir ses
            üretirdi. */}
        {owner && (
          <FormField
            label={t('todo.searchUser')}
            htmlFor="todo-share-user"
            error={error ?? undefined}
          >
            <UserMultiPicker
              inputId="todo-share-user"
              value={selected}
              onChange={setSelected}
              search={(q) => searchTodoShareUsers(q, listId as number)}
              placeholder={t('todo.searchUserHint')}
              disabled={add.isPending}
              invalid={!!error}
              describedBy="todo-share-user-err"
            />
          </FormField>
        )}

        {viewers.length === 0 && !shares.isLoading && (
          <p className="todo-share-empty">{t('todo.noViewers')}</p>
        )}
        <ul className="todo-share-list">
          {viewers.map((v) => (
            <li key={v.id} className="todo-share-row">
              <span className="todo-share-name">{v.fullName}</span>
              {v.employeeId && <span className="todo-share-sicil">{v.employeeId}</span>}
              {/* Çıkarma düğmesi YALNIZ sahipte (kullanıcı kararı 01.10.2026): çıkarma bir YÖNETİM
                  işlemidir. Kullanıcının kendi satırında da gizlenir — sunucu orada da 404 döner,
                  düğmeyi bırakmak yapamayacağı bir işe yöneltmek olurdu. */}
              {owner && (
                <Button
                  icon="pi pi-times"
                  rounded
                  text
                  severity="danger"
                  disabled={remove.isPending}
                  aria-label={rowActionLabel(t, 'todo.removeShare', v.fullName)}
                  title={rowActionLabel(t, 'todo.removeShare', v.fullName)}
                  onClick={() => confirmRemove(v)}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </FormDialog>
  )
}
